/**
 * 错词本 —— `/students/:id/dictation/mistakes`。
 *
 * ## 用户需求原话
 *
 * > "把没有听写出来或者错的词，在系统里面登记，为下一次再一次去听写这些错误的词
 * > 做好登记，这样的话就可以系统性地去听写这些词语。"
 *
 * 这一页就是那个"登记本"：按孩子看、能看到"错了几次 / 上次什么时候错 /
 * 下次什么时候复习"，能筛"今天该复习的"，能**一键开始一次只听错词的听写**，
 * 也能手动把某个词标成"已掌握"。
 *
 * ## 三条设计约束（都是本项目踩过的坑）
 *
 *   1. **0 / 拿不到 / 空 三者必须分得开**：`mistakes === null` 是"这份数据没拿到"，
 *      与"错词本是空的"是两件事（`describeListState` 已经在做这件事）。
 *   2. **排程规则要说人话**，而且那句话来自**后端**（`ladderNote`，唯一出处是
 *      shared 的 `describeReviewLadder()`）—— 前端不另写一份，免得两处漂移成
 *      "界面说 3 天、库里排 7 天"（那正是家长最会察觉、也最伤信任的错）。
 *   3. **手机优先**：每条错词一行大字，操作按钮 `shrink-0`，窄屏不横向滚动。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import { IconCheck, IconPlay, IconRotateCcw } from '../../components/ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import { countLabel, describeListState } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import type { MistakeView } from '../../lib/api/dictationSessions';
import { formatDateTimeFull } from '../../lib/utils';
import {
  actionableWordIds,
  describeMistakeCounts,
  describeMistakeDue,
  describeMistakeHeader,
  sortMistakes,
} from '../../lib/dictationMistakes';

export function ParentDictationMistakesPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  /** 只看今天该复习的（默认关：错词本默认显示**全部**还没掌握的词）。 */
  const [dueOnly, setDueOnly] = useState(false);
  /** 也显示已掌握的（默认关）。 */
  const [includeResolved, setIncludeResolved] = useState(false);

  const mistakesQuery = useQuery({
    queryKey: ['dictation-mistakes', id, dueOnly, includeResolved] as const,
    queryFn: () => sessionApi.listMistakes(id, { dueOnly, includeResolved, limit: 200 }),
    enabled: id !== '',
  });

  const data = mistakesQuery.data;
  const list = data?.mistakes ?? null;
  const sorted = useMemo(() => sortMistakes(list ?? []), [list]);
  const openItems = sorted.filter((item) => item.resolvedAt === null);
  const resolvedItems = sorted.filter((item) => item.resolvedAt !== null);

  const resolveMutation = useMutation({
    mutationFn: (input: { wordIds: string[]; resolved: boolean }) =>
      sessionApi.setMistakeResolved(id, input.wordIds, input.resolved),
    onSuccess: (result, input) => {
      /**
       * 幂等要**说出来**：已经消解的词再点一次时 `changed = 0`，
       * 界面必须如实告诉家长"这个词本来就是这个状态"，而不是假装成功。
       */
      if (result.changed === 0) {
        toast.showToast(
          input.resolved ? '这些词本来就已经是「已掌握」' : '这些词本来就在错词本里',
          'info',
        );
      } else {
        toast.showToast(
          input.resolved
            ? `已标记 ${result.changed} 个词为「已掌握」`
            : `已把 ${result.changed} 个词放回复习`,
          'success',
        );
      }
      void qc.invalidateQueries({ queryKey: ['dictation-mistakes', id] });
    },
    onError: (error: unknown) => {
      toast.showToast(`没保存上：${toErrorMessage(error)}`, 'error');
    },
  });

  const startMutation = useMutation({
    mutationFn: (mode: 'due_only' | 'all') =>
      sessionApi.startSessionFromMistakes(id, { mode, limit: 50, title: '' }),
    onSuccess: (session) => {
      toast.showToast(`已布置 ${session.questions?.length ?? 0} 个词的复习`, 'success');
      void qc.invalidateQueries({ queryKey: ['dictation-sessions', id] });
      /** 直接进播放器 —— 家长点这一下的意图就是"现在练"。 */
      navigate(`/students/${id}/dictation/${session.id}`);
    },
    onError: (error: unknown) => {
      toast.showToast(`没能布置复习：${toErrorMessage(error)}`, 'error');
    },
  });

  const state = describeListState(list, data?.total ?? null);
  const dueCount = data?.dueCount ?? null;
  const openCount = data?.openCount ?? null;
  const canStart = (dueCount ?? 0) > 0 || (openCount ?? 0) > 0;

  return (
    <AppShell kind="parent" title="错词本">
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-slate-900">需要重听的词</h2>
              <p className="mt-1 text-sm text-slate-600">
                {describeMistakeHeader(openCount, dueCount, data?.total ?? null)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <Badge tone={openCount === null ? 'warn' : openCount === 0 ? 'success' : 'brand'}>
                没掌握 {countLabel(openCount)}
              </Badge>
              <Badge tone={(dueCount ?? 0) > 0 ? 'warn' : 'neutral'}>
                今天该复习 {countLabel(dueCount)}
              </Badge>
            </div>
          </div>

          {/*
            排程规则的人话说明。它来自**后端**（唯一出处是 shared 的
            `describeReviewLadder()`），前端只转发 —— 两处各写一份迟早会漂移，
            而"系统是不是在偷偷乱排"这个问题一旦冒出来，这个功能就失去信任了。
            拿不到时**明说没拿到**，不编一句。
          */}
          <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {data?.ladderNote ?? '复习规则说明没拿到（刷新一次试试）—— 复习时间本身仍然有效。'}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={dueOnly}
                onChange={(event) => setDueOnly(event.target.checked)}
              />
              只看今天该复习的
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={includeResolved}
                onChange={(event) => setIncludeResolved(event.target.checked)}
              />
              也显示已掌握的
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              disabled={!canStart || startMutation.isPending}
              loading={startMutation.isPending}
              onClick={() => startMutation.mutate('due_only')}
            >
              <span className="mr-1 inline-flex">
                <IconPlay size={16} />
              </span>
              现在复习今天该复习的
            </Button>
            <Button
              variant="secondary"
              disabled={!canStart || startMutation.isPending}
              onClick={() => startMutation.mutate('all')}
            >
              复习全部没掌握的
            </Button>
            <Link to={`/students/${id}/dictation`}>
              <Button variant="ghost" size="sm">
                回听写页
              </Button>
            </Link>
          </div>
          {!canStart && openCount === 0 && (
            <p className="mt-2 text-xs text-slate-500">
              这里没有可复习的词 —— 先批改一次听写，错的词会自动进来。
              （现在按会布置出一次**一个词都没有**的听写，所以按钮就是灰的。）
            </p>
          )}
        </Card>

        <Card>
          <CardHeader
            title={includeResolved ? '全部登记（含已掌握）' : '还没掌握的词'}
            description="按「下次该复习的时间」排序，最该复习的排最前。"
          />

          {mistakesQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : mistakesQuery.isError ? (
            <ErrorState
              message={`没拿到错词本：${toErrorMessage(mistakesQuery.error)}`}
              onRetry={() => void mistakesQuery.refetch()}
            />
          ) : state.state === 'unknown' ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {state.message}
              <Button
                size="sm"
                variant="secondary"
                className="ml-2"
                onClick={() => void mistakesQuery.refetch()}
              >
                重新取一次
              </Button>
            </p>
          ) : sorted.length === 0 ? (
            <EmptyState
              icon="🎉"
              title={dueOnly ? '今天没有要复习的错词' : '错词本还是空的'}
              description={
                dueOnly
                  ? '取消上面的「只看今天该复习的」，就能看到以后要复习的词。'
                  : '批改一次听写，写错或没写出来的词就会自动登记在这里，并按 1 → 3 → 7 天排下次复习。'
              }
            />
          ) : (
            <ul className="space-y-2">
              {openItems.map((mistake) => (
                <MistakeRow
                  key={mistake.id}
                  mistake={mistake}
                  busy={resolveMutation.isPending}
                  onResolve={() => {
                    if (mistake.wordId === null) return;
                    resolveMutation.mutate({ wordIds: [mistake.wordId], resolved: true });
                  }}
                />
              ))}
              {resolvedItems.length > 0 && (
                <li className="pt-2 text-xs font-medium text-slate-500">
                  已掌握（{resolvedItems.length}）—— 手滑标错了可以放回复习
                </li>
              )}
              {resolvedItems.map((mistake) => (
                <MistakeRow
                  key={mistake.id}
                  mistake={mistake}
                  busy={resolveMutation.isPending}
                  onResolve={() => {
                    if (mistake.wordId === null) return;
                    resolveMutation.mutate({ wordIds: [mistake.wordId], resolved: false });
                  }}
                />
              ))}
            </ul>
          )}
        </Card>

        {openItems.length > 1 && (
          <Card className="bg-slate-50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="min-w-0 flex-1 text-xs text-slate-500">
                想看错得最多的词？按下面的按钮把这一页都标成已掌握 ——
                下次批改里如果又错了，它会自动回到错词本（错误次数会继续累加，不会被清零）。
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0"
                disabled={resolveMutation.isPending}
                onClick={() =>
                  resolveMutation.mutate({
                    wordIds: actionableWordIds(openItems),
                    resolved: true,
                  })
                }
              >
                把这一页都标成已掌握
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/*
        时间口径提示：家长最常问的是"下次什么时候"。时间用**后端给的 ISO 时间戳**
        渲染（`formatDateTimeFull` 与全站同一套：'YYYY-MM-DD HH:mm' + 家庭时区），
        所以不会出现"显示明天、实际是今天"。
      */}
      <p className="mt-3 px-1 text-xs text-slate-400">
        复习时间排在家里所在时区的下午 4 点（放学后、晚饭前）。到期只是一个"该练了"
        的提示：想提前练随时可以，错词本里的词也可以直接开始一次听写。
      </p>
    </AppShell>
  );
}

/** 一条错词：词面 + 拼音 + 计数 + 时间 + 动作。 */
function MistakeRow({
  mistake,
  busy,
  onResolve,
}: {
  mistake: MistakeView;
  busy: boolean;
  onResolve: () => void;
}) {
  const resolved = mistake.resolvedAt !== null;
  return (
    <li
      className={
        resolved
          ? 'flex flex-wrap items-start justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 opacity-80'
          : 'flex flex-wrap items-start justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2.5'
      }
    >
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2">
          <span className="truncate text-base font-semibold text-slate-900">
            {mistake.wordText}
          </span>
          {mistake.wordId === null && <Badge tone="warn">这个词已从字表删除</Badge>}
        </p>
        <p className="truncate font-mono text-xs text-slate-500">
          {mistake.pinyin === '' ? '（没有读音）' : mistake.pinyin}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {describeMistakeCounts(mistake)}
          {mistake.lastWrongAt === null
            ? ''
            : ` · 上次错在 ${formatDateTimeFull(mistake.lastWrongAt)}`}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {/*
            三种情况分开说（已掌握 / 今天该复习 / 排在第几天档），
            拿不到 `due` 时说「—」而不是"没到期"。
          */}
          {describeMistakeDue(mistake)}
          {mistake.nextReviewAt === null
            ? ''
            : ` · ${formatDateTimeFull(mistake.nextReviewAt)}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {resolved ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={onResolve}>
            <span className="mr-1 inline-flex">
              <IconRotateCcw size={14} />
            </span>
            放回复习
          </Button>
        ) : (
          <Button size="sm" variant="secondary" disabled={busy || mistake.wordId === null} onClick={onResolve}>
            <span className="mr-1 inline-flex">
              <IconCheck size={14} />
            </span>
            已掌握
          </Button>
        )}
      </div>
    </li>
  );
}
